import React from 'react';
import { Form, Input } from '@crisellevaldez/speculo-ui';
import { ExternalLink, Scale } from 'lucide-react';
import { IconPill } from '../../../../common/IconPill';
import { getConfig } from '../../../../../config/environment';
import { isValidLegalSlug, legalPageUrl } from '../../../../../utils/legalSlug';

interface ThirdPartyIdxSectionProps {
  organizationId: string;
  /** The slug currently saved, if any; the links below open its pages. */
  savedSlug: string | null;
  value: string;
  onChange: (value: string) => void;
}

/**
 * For orgs on a third-party IDX: the slug of their Terms of Use and Privacy Policy pages on
 * remi.homes. Short-link consent links the org-id address, which redirects to the current slug,
 * so the slug can be changed at any time.
 */
export const ThirdPartyIdxSection: React.FC<ThirdPartyIdxSectionProps> = ({
  organizationId,
  savedSlug,
  value,
  onChange,
}) => {
  const slug = value.trim();
  const invalid = slug !== '' && !isValidLegalSlug(slug);
  const baseUrl = getConfig().remiLinksBaseUrl;

  return (
    <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
      <div className="flex items-center gap-3 mb-3">
        <IconPill icon={Scale} />
        <h4 className="text-sm font-bold text-foreground">Third Party IDX</h4>
      </div>

      <hr className="dark:border-white/10" />

      <div className="mt-5 space-y-4">
        <Form.Control>
          <Form.Label>Short Links Legal Pages Slug</Form.Label>
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g., fun-realtor-florida"
            aria-invalid={invalid}
          />
          {invalid ? (
            <Form.HelperText className="text-red-500">
              Use lowercase letters, numbers and single hyphens, e.g. fun-realtor-florida.
            </Form.HelperText>
          ) : (
            <Form.HelperText>
              The address of this org&apos;s Terms of Use and Privacy Policy pages. Links already sent
              in short-link consent text keep working after a change.
            </Form.HelperText>
          )}
        </Form.Control>

        <div className="flex flex-wrap gap-4 text-sm">
          {(['terms', 'privacy'] as const).map((document) => (
            <a
              key={document}
              href={legalPageUrl(baseUrl, organizationId, savedSlug, document)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline underline-offset-2"
            >
              {document === 'terms' ? 'Terms of Use' : 'Privacy Policy'}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};
